import { ExpansionPanelProps, FormControlLabelProps, SwitchProps } from '@material-ui/core';
import { ReactNode, RefObject } from 'react';
import { Employee } from '../../../state/ducks/company/types';
import { FBInputType, FBLabelLocaleProps, FBOption } from '../../form.builder';

export enum GeneralSettingsTabs {
  Printing = 'Printing',
  AccountCodes = 'Account Codes',
  Navigation = 'Navigation',
  APITokens = 'API Tokens',
  AR = 'Approval Requests',
  MD = 'Material Disposition',
  ASLStatus = 'ASL Status',
  SupplierConfiguration = 'Supplier Configuration',
  LCP = 'Life Cycle Phases',
  LotAutoAllocationOrder = 'Lot Auto-allocation Order',
  ExpiredLots = 'Expired Lots',
  Integrations = 'Intergrations',
  EF = 'Equipment Family',
  ApprovalRoles = 'Approval Roles',
  Locations = 'Locations',
  MaterialFlow = 'Material Flow',
  SystemTransactions = 'System Transactions Setup',
  Training = 'Training',
  Timezone = 'Timezone',
  LHRBuild = 'LHR Build',
  LHRBuildSwitch = 'LHR Build Switch',
  TaxRate = 'Tax Rate',
  CreationPermissions = 'CREATION PERMISSIONS',
  ApprovalConfigurations = 'Approval Configuration',
  WorkflowConfigurations = 'Workflow Configuration',
  PreviousRevisionDownloads = 'Previous Revision Downloads',
}

export interface GeneralSettingsProps {
  isLoading: boolean
  updatedAt?: string
  onSave: () => void
}

export interface SettingsPanelProps extends ExpansionPanelProps {
  title: string
  info?: string
  containerRef?: RefObject<HTMLElement>
  isOutOfSync?: boolean
  addButtonLabel?: string
  onToggle: () => void
  onAddNew?: () => void
  onManageItems?: () => void
}

export interface SwitchControlProps extends SwitchProps {
  label: ReactNode
  labelProps?: Partial<FormControlLabelProps>
}

export interface HeaderLastSavedProps {
  timestamp: string
  loading?: boolean
  updateInterval?: number
}

export enum AUDIT_CHANGE_TYPE {
  DELETED = 'DELETED',
  ADDED = 'ADDED',
  UPDATED = 'UPDATED',
}

export interface CompanySettingsChangeLog {
  id: string
  companyId: string
  ownerId: string
  ownerName: string
  ownerEmail: string
  parentKey: string
  parentKeyLabel: string
  fieldChanged: string
  changeType?: AUDIT_CHANGE_TYPE
  displayText: string
  updatedAt: string
  groupId: string
}

export enum REQUIRED_APPROVERS {
  ANY_ONE = 'any_one',
  ANY_TWO = 'any_two',
  ALL = 'all',
}

export enum APPROVAL_STAGE_STATUS {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

export enum APPROVER_TYPE {
  USER = 'user',
  GROUP = 'group',
}

export interface SelectOption {
  id: string
  label?: string
}

export interface EmployeeOption {
  id: string
  name: string | undefined
  email: string | undefined
  satisfyingGroups: string[]
}

export interface GroupOption {
  id: string
  name: string | undefined
  joinedEmployees: Employee[]
}

export interface SourceMapping {
  label?: string | ReactNode | FBLabelLocaleProps
  name: string
  type?: FBInputType
  options?: Array<Record<any, any>> | FBOption[] | string[]
}

export interface ApprovalConfigurationStageApprover {
  id: string
  name?: string
  type: APPROVER_TYPE
  isOptional: boolean
  employeeCount?: number
}

export interface ApprovalConfigurationStage {
  stageNumber: number
  name: string
  assignedApprovers: ApprovalConfigurationStageApprover[]
  requiredCount: REQUIRED_APPROVERS
  mappedFrom?: SourceMapping[]
  editableFields?: string[]
  stageOwnerId?: string
  optionalApproverRejectionRejectsStage: boolean
}

export interface StageApprover {
  approverId: string
  approverName?: string
  isOptional: boolean
  addedVia?: {
    groupId: string
    groupName: string
  }
}
export interface ApprovalStage {
  id?: number
  stageNumber: number
  name: string
  assignedApprovers: StageApprover[]
  requiredCount: REQUIRED_APPROVERS
  editableFields?: string[]
  status?: APPROVAL_STAGE_STATUS
  stageOwnerId?: string
  optionalApproverRejectionRejectsStage: boolean
}

export interface SaveApprovalConfigurationBody {
  approvalRequestId?: string
  documentRevisionId?: string
  name?: string
  description?: string
  approvalStages: ApprovalStage[]
  id?: string
  isUnassign?: boolean
}

export interface StageComparison {
  hasChanges: boolean
  addedStages: ApprovalConfigurationStage[]
  deletedStages: ApprovalConfigurationStage[]
  updatedStages: UpdatedStage[]
}

export interface UpdatedStage {
  stageNumber: number
  requiredCountChanged?: {
    old: REQUIRED_APPROVERS
    current: REQUIRED_APPROVERS
  }
  optionalApproverRejectionRejectsStage?: {
    old: boolean
    current: boolean
  }
  name?: {
    old: string
    current: string
  }
  approversAdded: ApprovalConfigurationStageApprover[]
  approversDeleted: ApprovalConfigurationStageApprover[]
  approversUpdated: Array<{
    approverId: string
    approverName?: string
    changes: {
      isOptional?: {
        old: boolean
        current: boolean
      }
    }
  }>
}

export interface StageStatus {
  stageNumber: number
  isDone: boolean
  approvedCount: number
}

export interface WorkflowStatusDetails {
  currentStageInProgress: number
  isAllStagesComplete: boolean
  stageStatusDetails: StageStatus[]
}

export const toSortedOptions = <T>(
  items: T[],
  getId: (item: T) => string,
  getText: (item: T) => string,
  getExtra?: (item: T) => Record<string, unknown>,
) =>
  items
    .slice()
    .sort((a, b) =>
      getText(a).localeCompare(
        getText(b),
        undefined,
        { sensitivity: 'base' },
      ),
    )
    .map((item) => ({
      id: getId(item),
      text: getText(item),
      ...(getExtra?.(item) ?? {}),
    }));
